/**
 * Spec 218 PR3 — Invoice detail (`/invoices/:id`).
 *
 * Read-only surface mirroring `mobile/src/screens/InvoiceDetailScreen.tsx`:
 *
 *   - Header: series-number, issuance date, AEAT status badge.
 *   - Recipient block: name + NIF + email + address.
 *   - Lines table: description / base / VAT% / VAT€ / total.
 *   - Totals block: base, VAT total, grand total.
 *   - AEAT block: hash (truncated, copy on click), QR (decode'd
 *     base64 inline), Verifacti UUID.
 *   - Audit footer: created/updated timestamps + cancelled flag.
 *
 * No write actions in this PR — emit / cancel / rectify ship in
 * 218 PR4 + PR6. PDF download lives in 218 PR5.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Ban, ChevronLeft, Copy, Download, FilePlus, FileText, Hash, Receipt, ShieldCheck } from 'lucide-react';

import { EmptyState } from '../components/EmptyState';
import { SkeletonList } from '../components/Skeleton';
import {
  cancelInvoice,
  getFiscalProfile,
  getInvoice,
  InvoicingError,
  type EmitResponse,
  type InvoicingCtx,
} from '../sync/invoicing';
import { downloadInvoicePdf } from '../utils/invoicePdf';
import type { FiscalProfile, Invoice, InvoiceDraft } from '@cuadrilla/types/invoice';
import type { WebIdentity } from '../store/keys';

import './InvoiceDetail.css';

interface InvoiceDetailProps {
  identity: WebIdentity;
}

const STATUS_LABEL: Record<string, string> = {
  accepted: 'Aceptada por AEAT',
  pending: 'Pendiente AEAT',
  rejected: 'Rechazada por AEAT',
  failed: 'Error en envío',
  cancelled: 'Anulada',
};

const STATUS_TONE: Record<string, string> = {
  accepted: 'invoice-status--ok',
  pending: 'invoice-status--pending',
  rejected: 'invoice-status--err',
  failed: 'invoice-status--err',
  cancelled: 'invoice-status--cancelled',
};

function buildCtx(identity: WebIdentity): InvoicingCtx {
  return { companyToken: identity.companyToken };
}

function formatEur(amount: number): string {
  // Relay stores in_total as NUMERIC(14,2) in EUROS (e.g. 121.00 for a
  // 100 € + 21% IVA invoice). Earlier PR3 wrongly divided by 100 here
  // and in InvoiceList — that produced "1,21 €" for a real 121 € row.
  // Spec 218 PR5 fixes the unit handling end-to-end so detail, list and
  // PDF all agree.
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
}

function formatDate(input: string | number | undefined): string {
  if (!input) return '—';
  const ts = typeof input === 'string' ? Date.parse(input) : input;
  if (!Number.isFinite(ts)) return String(input);
  return new Date(ts).toLocaleString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function InvoiceDetail({ identity }: InvoiceDetailProps) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [invoice, setInvoice] = useState<(Invoice & { draft: InvoiceDraft }) | null>(null);
  const [profile, setProfile] = useState<FiscalProfile | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [copied, setCopied] = useState<'hash' | 'uuid' | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setInvoice(null);
    setLoadError(null);
    const ctx = buildCtx(identity);
    void Promise.all([
      getInvoice(ctx, id),
      getFiscalProfile(ctx).catch(() => null),
    ]).then(
      ([inv, prof]) => {
        if (cancelled) return;
        setInvoice(inv);
        setProfile(prof);
      },
      (err) => {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : String(err));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [identity, id, reloadKey]);

  async function onCancel() {
    if (!invoice || cancelling) return;
    // Spec 218 PR6 — burning the invoice number is irreversible: AEAT
    // refuses re-cancel and the series-number tuple cannot be reused.
    // Use the native confirm() to keep the destructive action explicit
    // without dragging in a custom modal.
    const ok = window.confirm(
      `Vas a anular ${invoice.series}-${invoice.number}. Esta acción no se puede deshacer y el número queda inutilizable. ¿Confirmas?`,
    );
    if (!ok) return;
    setCancelling(true);
    setCancelError(null);
    try {
      await cancelInvoice(buildCtx(identity), invoice.id);
      // Force a reload so the cancelled flag, status pill and disabled
      // state pick up the relay's new row state.
      setReloadKey((k) => k + 1);
    } catch (err) {
      const msg =
        err instanceof InvoicingError && err.code === 'NETWORK'
          ? 'Sin conexión. Vuelve a intentarlo cuando recuperes señal.'
          : err instanceof Error
            ? err.message
            : String(err);
      setCancelError(msg);
    } finally {
      setCancelling(false);
    }
  }

  function onRectify() {
    if (!invoice) return;
    // Spec 186 (mobile) — the rectification flow is just an emit with
    // `draft.rectification` set. We forward the whole Invoice via
    // location.state; EmitInvoice already knows how to seed type=R1,
    // prefix the description, and copy the lines.
    navigate('/invoices/new', { state: { rectificationOf: invoice } });
  }

  async function onDownloadPdf() {
    if (!invoice || !profile) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      // Reconstruct the EmitResponse shape from the persisted invoice so
      // the same template (mobile uses on emission) renders an identical
      // PDF on demand from the detail screen.
      const response: EmitResponse = {
        id: invoice.id,
        verifactiUuid: invoice.verifactiUuid,
        qrBase64: invoice.qrBase64,
        qrUrl: invoice.qrUrl,
        hash: invoice.hash,
        aeatStatus: invoice.aeatStatus,
        total: invoice.total,
      };
      await downloadInvoicePdf({
        profile,
        draft: invoice.draft,
        response,
        // Cancelled invoices download with an "ANULADA" watermark + banner
        // so a recipient can't be misled by a clean copy of the original.
        cancelled: invoice.cancelled === true,
      });
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : String(err));
    } finally {
      setDownloading(false);
    }
  }

  function copyText(value: string, label: 'hash' | 'uuid') {
    void navigator.clipboard?.writeText(value).then(
      () => {
        setCopied(label);
        setTimeout(() => setCopied(null), 2000);
      },
      () => {
        // Clipboard denied — silent.
      },
    );
  }

  const lines = (invoice as unknown as { lines?: InvoiceDraft['lines'] })?.lines ?? [];
  const totalsLine = useMemo(() => {
    if (!invoice) return null;
    const r = invoice as unknown as { base?: number; vat?: number; total?: number };
    return {
      base: r.base ?? 0,
      vat: r.vat ?? 0,
      total: r.total ?? invoice.total ?? 0,
    };
  }, [invoice]);

  if (loadError) {
    return (
      <main id="main-content" className="id-screen" data-testid="invoice-detail-screen">
        <Link to="/invoices" className="id-back" data-testid="invoice-detail-back">
          <ChevronLeft size={20} strokeWidth={2} aria-hidden="true" />
          Facturas
        </Link>
        <EmptyState
          testId="invoice-detail-error"
          variant="noResults"
          title="No se pudo cargar la factura"
          body={<>{loadError}</>}
        />
      </main>
    );
  }

  if (!invoice) {
    return (
      <main id="main-content" className="id-screen" data-testid="invoice-detail-screen">
        <SkeletonList count={4} testId="invoice-detail-loading" />
      </main>
    );
  }

  const status = invoice.cancelled ? 'cancelled' : invoice.aeatStatus;
  const recipientLabel = (invoice as unknown as { recipient?: { name?: string } }).recipient?.name ?? 'Sin destinatario';

  return (
    <main id="main-content" className="id-screen" data-testid="invoice-detail-screen">
      <Link to="/invoices" className="id-back" data-testid="invoice-detail-back">
        <ChevronLeft size={20} strokeWidth={2} aria-hidden="true" />
        Facturas
      </Link>

      <header className="id-header">
        <div className="id-header-title">
          <FileText size={24} strokeWidth={2} aria-hidden="true" />
          <h1 className="id-h1" data-testid="invoice-detail-number">
            {(invoice as unknown as { series?: string }).series}-{(invoice as unknown as { number?: string }).number}
          </h1>
          <span
            className={`invoice-status ${STATUS_TONE[status] ?? ''}`}
            data-testid="invoice-detail-status"
          >
            {STATUS_LABEL[status] ?? status}
          </span>
        </div>
        <p className="id-header-subtitle">
          Emitida el {formatDate((invoice as unknown as { issueDate?: string }).issueDate)} ·{' '}
          {recipientLabel}
        </p>
        <div className="id-actions">
          <button
            type="button"
            className="id-action-pdf"
            onClick={onDownloadPdf}
            disabled={!profile || downloading}
            data-testid="invoice-detail-download"
          >
            <Download size={18} strokeWidth={2} aria-hidden="true" />
            <span>{downloading ? 'Generando…' : 'Descargar PDF'}</span>
          </button>
          {/*
            Cancel + rectify are mutually exclusive end-states: AEAT rejects
            both on a row that has already been processed once. We disable
            the buttons rather than hide them so the user understands the
            options exist but are unavailable for THIS invoice.
          */}
          <button
            type="button"
            className="id-action-rectify"
            onClick={onRectify}
            disabled={invoice.cancelled || Boolean(invoice.rectifiedByInvoiceId)}
            data-testid="invoice-detail-rectify"
            title={
              invoice.cancelled
                ? 'No se puede rectificar una factura anulada'
                : invoice.rectifiedByInvoiceId
                  ? 'Esta factura ya tiene una rectificativa'
                  : undefined
            }
          >
            <FilePlus size={18} strokeWidth={2} aria-hidden="true" />
            <span>Rectificar</span>
          </button>
          <button
            type="button"
            className="id-action-cancel"
            onClick={onCancel}
            disabled={invoice.cancelled || cancelling}
            data-testid="invoice-detail-cancel"
            title={invoice.cancelled ? 'Esta factura ya está anulada' : undefined}
          >
            <Ban size={18} strokeWidth={2} aria-hidden="true" />
            <span>{cancelling ? 'Anulando…' : 'Anular factura'}</span>
          </button>
        </div>
        {downloadError && (
          <p className="id-download-error" role="alert" data-testid="invoice-detail-download-error">
            No se pudo generar el PDF: {downloadError}
          </p>
        )}
        {cancelError && (
          <p className="id-download-error" role="alert" data-testid="invoice-detail-cancel-error">
            No se pudo anular la factura: {cancelError}
          </p>
        )}
      </header>

      <section className="id-card" aria-label="Destinatario" data-testid="invoice-detail-recipient">
        <h2 className="id-h2">
          <Receipt size={20} strokeWidth={2} aria-hidden="true" />
          Destinatario
        </h2>
        {(() => {
          const recipient = (invoice as unknown as { recipient?: { name?: string; nif?: string; email?: string; address?: string } }).recipient;
          if (!recipient) return <p className="id-empty">Sin destinatario informado.</p>;
          return (
            <dl className="id-dl">
              {recipient.name && (
                <>
                  <dt>Nombre</dt>
                  <dd>{recipient.name}</dd>
                </>
              )}
              {recipient.nif && (
                <>
                  <dt>NIF / CIF</dt>
                  <dd>{recipient.nif}</dd>
                </>
              )}
              {recipient.email && (
                <>
                  <dt>Email</dt>
                  <dd>{recipient.email}</dd>
                </>
              )}
              {recipient.address && (
                <>
                  <dt>Dirección</dt>
                  <dd>{recipient.address}</dd>
                </>
              )}
            </dl>
          );
        })()}
      </section>

      {lines.length > 0 && (
        <section className="id-card" aria-label="Líneas">
          <h2 className="id-h2">
            <FileText size={20} strokeWidth={2} aria-hidden="true" />
            Líneas
          </h2>
          <table className="id-lines">
            <thead>
              <tr>
                <th>Descripción</th>
                <th>Base</th>
                <th>IVA %</th>
                <th>IVA €</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, i) => {
                const base = (line as unknown as { base?: number }).base ?? 0;
                const vatRate = (line as unknown as { vatRate?: number }).vatRate ?? 0;
                const vat = Math.round(base * (vatRate / 100));
                return (
                  <tr key={`${(line as unknown as { id?: string }).id ?? i}`}>
                    <td>{(line as unknown as { description?: string }).description ?? ''}</td>
                    <td>{formatEur(base)}</td>
                    <td>{vatRate}%</td>
                    <td>{formatEur(vat)}</td>
                    <td>{formatEur(base + vat)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      {totalsLine && (
        <section className="id-card id-totals" aria-label="Totales" data-testid="invoice-detail-totals">
          <div>
            <span className="id-totals-label">Base imponible</span>
            <span className="id-totals-value">{formatEur(totalsLine.base)}</span>
          </div>
          <div>
            <span className="id-totals-label">IVA</span>
            <span className="id-totals-value">{formatEur(totalsLine.vat)}</span>
          </div>
          <div className="id-totals-grand">
            <span className="id-totals-label">Total</span>
            <span className="id-totals-value">{formatEur(totalsLine.total)}</span>
          </div>
        </section>
      )}

      <section className="id-card" aria-label="AEAT" data-testid="invoice-detail-aeat">
        <h2 className="id-h2">
          <ShieldCheck size={20} strokeWidth={2} aria-hidden="true" />
          AEAT / Verifacti
        </h2>
        {(() => {
          const r = invoice as unknown as { hash?: string; verifactiUuid?: string; qrBase64?: string };
          return (
            <dl className="id-dl">
              {r.hash && (
                <>
                  <dt>Hash</dt>
                  <dd className="id-mono">
                    <span title={r.hash}>{r.hash.slice(0, 12)}…{r.hash.slice(-8)}</span>
                    <button
                      type="button"
                      className="id-copy"
                      onClick={() => copyText(r.hash!, 'hash')}
                      aria-label="Copiar hash"
                      data-testid="invoice-detail-hash-copy"
                    >
                      <Copy size={16} strokeWidth={2} aria-hidden="true" />
                      {copied === 'hash' ? '¡Copiado!' : 'Copiar'}
                    </button>
                  </dd>
                </>
              )}
              {r.verifactiUuid && (
                <>
                  <dt>Verifacti UUID</dt>
                  <dd className="id-mono">
                    <span>{r.verifactiUuid}</span>
                    <button
                      type="button"
                      className="id-copy"
                      onClick={() => copyText(r.verifactiUuid!, 'uuid')}
                      aria-label="Copiar UUID"
                      data-testid="invoice-detail-uuid-copy"
                    >
                      <Copy size={16} strokeWidth={2} aria-hidden="true" />
                      {copied === 'uuid' ? '¡Copiado!' : 'Copiar'}
                    </button>
                  </dd>
                </>
              )}
              {r.qrBase64 && (
                <>
                  <dt>QR sello</dt>
                  <dd>
                    <img
                      src={`data:image/png;base64,${r.qrBase64}`}
                      alt="QR del sello AEAT"
                      width={160}
                      height={160}
                      className="id-qr"
                      data-testid="invoice-detail-qr"
                    />
                  </dd>
                </>
              )}
            </dl>
          );
        })()}
      </section>

      <footer className="id-footer">
        <Hash size={16} strokeWidth={2} aria-hidden="true" />
        <span className="id-mono">{invoice.id}</span>
      </footer>
    </main>
  );
}
